// ============================================================================
// OUTPUTS TAB COMPONENT
// Main UI for project outputs management — Tailwind redesign
// ============================================================================

import React, { useState } from 'react';
import { OutputViewerModal } from './OutputViewerModal';
import { ConfirmModal } from '../common/ConfirmModal';
import {
  Eye, Download, Trash2, FileText, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Search, FileOutput, Database, Layers
} from 'lucide-react';
import {
  useProjectOutputs,
  useOutputContent,
  useDownloadOutput,
  useDeleteOutput,
  useFormatFileSize,
  useFormatOutputType,
  type ProjectOutput
} from '../../hooks/useOutputs';

interface OutputsTabProps {
  projectId: string;
  token: string;
}

type SortField = 'name' | 'type' | 'format' | 'createdAt' | 'fileSizeBytes' | 'sessionId';
type SortDirection = 'asc' | 'desc';

const TYPE_BADGE_CLASSES: Record<string, string> = {
  avatar: 'bg-purple-100 text-purple-800',
  workflow: 'bg-blue-100 text-blue-800',
  requirement: 'bg-emerald-100 text-emerald-800',
  user_story: 'bg-amber-100 text-amber-800',
  transcript: 'bg-blue-100 text-blue-800',
};

export const OutputsTab: React.FC<OutputsTabProps> = ({
  projectId,
  token
}) => {
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOutput, setSelectedOutput] = useState<ProjectOutput | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [outputToDelete, setOutputToDelete] = useState<ProjectOutput | null>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedOutputIds, setSelectedOutputIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Hooks
  const { data: outputs, isLoading, refetch } = useProjectOutputs(
    projectId,
    token,
    typeFilter ? { type: typeFilter } : undefined
  );
  const { data: content, isLoading: contentLoading } = useOutputContent(
    projectId,
    selectedOutput?.outputId || '',
    token,
    viewerOpen && !!selectedOutput
  );
  const downloadMutation = useDownloadOutput(projectId, token);
  const deleteMutation = useDeleteOutput(projectId, token);

  // Utility hooks
  const formatFileSize = useFormatFileSize();
  const formatOutputType = useFormatOutputType();
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleView = (output: ProjectOutput) => {
    setSelectedOutput(output);
    setViewerOpen(true);
  };

  const handleDownload = async (output: ProjectOutput) => {
    try {
      await downloadMutation.mutateAsync(output.outputId);
      showNotification('success', 'Download started');
    } catch (error: any) {
      showNotification('error', `Download failed: ${error.message}`);
    }
  };

  const handleDelete = (output: ProjectOutput) => {
    setOutputToDelete(output);
    setBulkDeleteMode(false);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (bulkDeleteMode) {
      if (selectedOutputIds.size === 0) return;

      let successCount = 0;
      let errorCount = 0;

      for (const outputId of Array.from(selectedOutputIds)) {
        try {
          await deleteMutation.mutateAsync(outputId);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      setDeleteConfirmOpen(false);
      setBulkDeleteMode(false);
      setSelectedOutputIds(new Set());
      refetch();

      if (errorCount === 0) {
        showNotification('success', `${successCount} output${successCount !== 1 ? 's' : ''} deleted successfully`);
      } else if (successCount === 0) {
        showNotification('error', `Failed to delete ${errorCount} output${errorCount !== 1 ? 's' : ''}`);
      } else {
        showNotification('success', `${successCount} deleted, ${errorCount} failed`);
      }
    } else {
      if (!outputToDelete) return;

      try {
        await deleteMutation.mutateAsync(outputToDelete.outputId);
        showNotification('success', 'Output deleted successfully');
        setDeleteConfirmOpen(false);
        setOutputToDelete(null);
        refetch();
      } catch (error: any) {
        showNotification('error', `Delete failed: ${error.message}`);
        setDeleteConfirmOpen(false);
        setOutputToDelete(null);
      }
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedOutputs.map(output => output.outputId));
      setSelectedOutputIds(allIds);
    } else {
      setSelectedOutputIds(new Set());
    }
  };

  const handleSelectOutput = (outputId: string, checked: boolean) => {
    const newSelected = new Set(selectedOutputIds);
    if (checked) {
      newSelected.add(outputId);
    } else {
      newSelected.delete(outputId);
    }
    setSelectedOutputIds(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedOutputIds.size === 0) return;
    setBulkDeleteMode(true);
    setDeleteConfirmOpen(true);
  };

  // Filter and sort outputs
  const filteredAndSortedOutputs = React.useMemo(() => {
    let filtered = outputs?.filter(output => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          output.name.toLowerCase().includes(searchLower) ||
          output.type.toLowerCase().includes(searchLower) ||
          output.avatarRole?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    }) || [];

    return [...filtered].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (aValue === undefined) aValue = '';
      if (bValue === undefined) bValue = '';

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [outputs, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalItems = filteredAndSortedOutputs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOutputs = filteredAndSortedOutputs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setSelectedOutputIds(new Set());
  }, [searchTerm, typeFilter, sortField, sortDirection]);

  const isAllSelected = paginatedOutputs.length > 0 &&
    paginatedOutputs.every(output => selectedOutputIds.has(output.outputId));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Compute stats
  const totalSize = outputs?.reduce((sum, o) => sum + o.fileSizeBytes, 0) || 0;
  const uniqueTypes = new Set(outputs?.map(o => o.type) || []).size;

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUp size={14} className="text-primary" />
      : <ChevronDown size={14} className="text-primary" />;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Notification */}
      {notification && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
          notification.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Outputs */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 font-medium text-sm">Total Outputs</span>
            <div className="size-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <FileOutput size={18} />
            </div>
          </div>
          <span className="text-3xl font-bold text-navy">{filteredAndSortedOutputs.length}</span>
        </div>
        {/* Total Size */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 font-medium text-sm">Total Size</span>
            <div className="size-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Database size={18} />
            </div>
          </div>
          <span className="text-3xl font-bold text-navy">{formatFileSize(totalSize)}</span>
        </div>
        {/* Output Types */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 font-medium text-sm">Output Types</span>
            <div className="size-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers size={18} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-navy">{uniqueTypes}</span>
            <span className="text-sm text-slate-400 font-medium">unique formats</span>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Search outputs by name, type, or avatar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full sm:w-48 bg-slate-50 border border-slate-200 text-slate-700 py-2.5 px-4 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors appearance-none cursor-pointer"
          >
            <option value="">All Types</option>
            <option value="avatar">Avatar Outputs</option>
            <option value="workflow">Workflow Results</option>
            <option value="requirement">Requirements</option>
            <option value="user_story">User Stories</option>
            <option value="transcript">Transcripts</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedOutputIds.size > 0 && (
        <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
          <span className="text-sm font-medium text-blue-700">
            {selectedOutputIds.size} item{selectedOutputIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedOutputIds(new Set())}
              className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-red-500 text-white border border-red-500 rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-16 text-slate-400">
          <p className="text-sm">Loading outputs...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredAndSortedOutputs.length === 0 && (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-xl shadow-sm">
          <FileText size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-navy mb-2">No Outputs Yet</h3>
          <p className="text-slate-400 text-sm">
            Outputs will be automatically created when sessions and workflows are completed.
          </p>
        </div>
      )}

      {/* Outputs Table */}
      {!isLoading && filteredAndSortedOutputs.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col flex-1 min-h-[400px]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left w-12" scope="col">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                    />
                  </th>
                  {([
                    ['name', 'Output Name'],
                    ['type', 'Type'],
                    ['format', 'Format'],
                    ['sessionId', 'Session'],
                    ['fileSizeBytes', 'Size'],
                    ['createdAt', 'Created'],
                  ] as [SortField, string][]).map(([field, label]) => (
                    <th
                      key={field}
                      onClick={() => handleSort(field)}
                      className="px-6 py-4 text-left text-xs font-semibold text-navy uppercase tracking-wider cursor-pointer select-none hover:text-primary transition-colors"
                      scope="col"
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        <SortIcon field={field} />
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-4 text-right text-xs font-semibold text-navy uppercase tracking-wider" scope="col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {paginatedOutputs.map((output) => (
                  <tr
                    key={output.outputId}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOutputIds.has(output.outputId)}
                        onChange={(e) => handleSelectOutput(output.outputId, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-navy">{output.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        TYPE_BADGE_CLASSES[output.type] || 'bg-slate-100 text-slate-800'
                      }`}>
                        {formatOutputType(output.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-mono border border-slate-200">
                        {output.format.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-mono">
                      {output.sessionId ? output.sessionId.substring(0, 12) + '...' : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatFileSize(output.fileSizeBytes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatDate(output.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleView(output)}
                          className="text-slate-400 hover:text-primary transition-colors p-1 rounded-md hover:bg-primary/10"
                          title="View output"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownload(output)}
                          className="text-slate-400 hover:text-primary transition-colors p-1 rounded-md hover:bg-primary/10"
                          title="Download output"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(output)}
                          className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50"
                          title="Delete output"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-slate-200 bg-white px-6 py-4 flex items-center justify-between mt-auto">
              <p className="text-sm text-slate-500">
                Showing <span className="font-medium text-navy">{startIndex + 1}-{Math.min(endIndex, totalItems)}</span> of <span className="font-medium text-navy">{totalItems}</span> outputs
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="py-1.5 px-2 border border-slate-200 rounded-lg text-xs bg-white text-slate-600 cursor-pointer"
                >
                  <option value="10">10 / page</option>
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                </select>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-primary hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-primary hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Output Viewer Modal */}
      <OutputViewerModal
        isOpen={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setSelectedOutput(null);
        }}
        output={selectedOutput}
        content={content || null}
        isLoading={contentLoading}
        onDownload={handleDownload}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setOutputToDelete(null);
          setBulkDeleteMode(false);
        }}
        onConfirm={confirmDelete}
        title={bulkDeleteMode ? "Delete Multiple Outputs" : "Delete Output"}
        message={
          bulkDeleteMode
            ? `Are you sure you want to delete ${selectedOutputIds.size} output${selectedOutputIds.size !== 1 ? 's' : ''}? This action cannot be undone.`
            : `Are you sure you want to delete "${outputToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </div>
  );
};
